import { Button, CommonProp } from '@acin/ui-core';
import React from 'react';
import { useMapControls } from '../../MapControlsProvider';
import { useConfirmInternalRegulationObligationRuleControlMappingsMutation } from '../graphql/mutations/confirmInternalRegulationObligationRuleControlMappings.generated';
import { useObligations } from '../hooks/useObligations';
import { useObligationRules } from '../hooks/useObligationRules';
import * as SC from './MapControlsFooter.styled';

export interface MapControlsFooterProps extends CommonProp {
  className?: string;
}

export const MapControlsFooter: React.FC<MapControlsFooterProps> = ({ className }) => {
  const {
    activeObligation,
    onUpdateActiveObligation,
    activeObligationRule,
    onUpdateActiveObligationRule,
    confirmedRuleIds,
    onConfirmRule,
    onMarkObligationReviewed,
  } = useMapControls();

  const { rules } = useObligationRules(activeObligation?.id);
  const { obligationsData } = useObligations(null);
  const obligations = obligationsData ?? [];

  const [confirmMappings, { loading }] = useConfirmInternalRegulationObligationRuleControlMappingsMutation();

  const handleConfirmNext = async () => {
    if (!activeObligation || !activeObligationRule) return;

    // Confirm the rule's control mappings on the server first; only advance on success.
    const { errors } = await confirmMappings({
      variables: { input: { regulationObligationRuleId: activeObligationRule.id } },
      errorPolicy: 'all',
    });
    // Leave the user on the current rule if the confirm failed.
    if (errors?.length) return;

    // Confirm the current rule's control mappings (or lack of).
    onConfirmRule(activeObligationRule.id);

    // If this was the last unconfirmed rule, the obligation is fully reviewed.
    const confirmedAfter = new Set(confirmedRuleIds);
    confirmedAfter.add(activeObligationRule.id);
    const allRulesConfirmed = rules.length > 0 && rules.every((rule) => confirmedAfter.has(rule.id));
    if (allRulesConfirmed) {
      onMarkObligationReviewed(activeObligation.id);
    }

    // Progress to the next rule, or the next obligation if on the last rule.
    const ruleIndex = rules.findIndex((rule) => rule.id === activeObligationRule.id);
    if (ruleIndex >= 0 && ruleIndex < rules.length - 1) {
      onUpdateActiveObligationRule(rules[ruleIndex + 1]);
      return;
    }

    const obligationIndex = obligations.findIndex((edge) => edge.node.id === activeObligation.id);
    const nextObligation = obligationIndex >= 0 ? obligations[obligationIndex + 1]?.node : undefined;
    if (nextObligation) {
      onUpdateActiveObligation(nextObligation);
      // Clear the active rule so the rules list auto-selects the first rule of the next obligation.
      onUpdateActiveObligationRule(null);
    }
  };

  return (
    <SC.Footer className={className}>
      <Button
        variant="secondary"
        label="Confirm & Next"
        disabled={!activeObligationRule || loading}
        onClick={handleConfirmNext}
      />
    </SC.Footer>
  );
};
